import { addDecimals } from "@/shared/utils/decimal";

import type { DashboardAlertType } from "./dashboardAlertCatalog";

/** Long enough to read the amount and reach the button while attending someone. */
export const DASHBOARD_ALERT_DURATION_MS = 12_000;

/**
 * Alerts of one kind arriving close together are one story, not a wall of toasts: a payout batch pays fifty
 * payouts in a minute, a busy till takes a payment every few seconds. The first two of a burst show on their
 * own; from the third, they are replaced by one summary toast that keeps counting ("12 envíos pagados") until
 * the burst goes quiet for `BURST_WINDOW_MS` — as long as a toast stays up, so a summary still on screen
 * keeps adding instead of starting a burst that would reset its count.
 */
export const BURST_WINDOW_MS = DASHBOARD_ALERT_DURATION_MS;
/** How many alerts of a burst show on their own before the summary takes over. */
export const BURST_SINGLES = 2;

export interface AlertBurst {
  type: DashboardAlertType;
  count: number;
  lastAt: number;
  /** The toasts shown on their own, for the summary to replace. */
  singles: readonly string[];
  /** Sum of the amounts while they share one currency; null once two currencies mix or one is unknown. */
  total: { amount: string; currency: string } | null;
}

export interface BurstStep {
  burst: AlertBurst;
  /** `single`: show this alert as its own toast. `summary`: show or update the burst's summary toast. */
  mode: "single" | "summary";
  /** Toasts to dismiss because the summary now stands for them. */
  dismiss: readonly string[];
  /** Whether it is news to the ear: every alert shown on its own, and the summary once, when it appears. */
  rings: boolean;
}

function addToTotal(
  total: AlertBurst["total"],
  amount: string | null,
  currency: string | null,
): AlertBurst["total"] {
  if (!total || !amount || !currency || total.currency !== currency) return null;
  try {
    return { amount: addDecimals(total.amount, amount), currency };
  } catch {
    // An amount that is not a decimal string: the summary goes without a total rather than not at all.
    return null;
  }
}

/** Adds one alert to its burst, starting a new burst when the last one went quiet. */
export function joinBurst(
  previous: AlertBurst | undefined,
  alert: { type: DashboardAlertType; toastId: string; amount: string | null; currency: string | null },
  now: number,
): BurstStep {
  const continues = !!previous && previous.type === alert.type && now - previous.lastAt <= BURST_WINDOW_MS;
  if (!continues) {
    return {
      burst: {
        type: alert.type,
        count: 1,
        lastAt: now,
        singles: [alert.toastId],
        total: alert.amount && alert.currency ? { amount: alert.amount, currency: alert.currency } : null,
      },
      mode: "single",
      dismiss: [],
      rings: true,
    };
  }
  const count = previous.count + 1;
  const total = addToTotal(previous.total, alert.amount, alert.currency);
  if (count <= BURST_SINGLES) {
    return {
      burst: { ...previous, count, lastAt: now, singles: [...previous.singles, alert.toastId], total },
      mode: "single",
      dismiss: [],
      rings: true,
    };
  }
  return {
    burst: { ...previous, count, lastAt: now, singles: [], total },
    mode: "summary",
    dismiss: previous.singles,
    rings: count === BURST_SINGLES + 1,
  };
}
